<template>
    <div
        class="relative flex flex-col basis-[calc(50%_-_0.625rem)] rounded-lg bg-secondary medium:basis-[calc(33.333%_-_0.875rem)] large:basis-[calc(50%_-_0.625rem)] xLarge:basis-[calc(33.333%_-_0.875rem)] xxLarge:basis-[calc(25%_-_0.9375rem)]"
        :class="{ 'border-2 border-primary box-border': productIsInBundle }"
    >
        <svg
            v-if="productIsInBundle"
            class="absolute top-[0.625rem] left-[0.625rem]"
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect width="28" height="28" rx="14" fill="#003B71" />
            <path d="M9 15.1364L11.6552 18L20 9" stroke="white" />
        </svg>
        <div class="p-[0.3125rem] medium:p-5">
            <img
                class="aspect-square object-cover m-auto"
                :src="productData.image_url"
                :srcset="`${productData.image_url_retina} 2x`"
                width="160"
                height="160"
                :alt="productData.name"
                loading="lazy"
            >
        </div>
        <div class="flex flex-col items-start justify-between gap-[0.625rem] h-full p-3 medium:pt-3 medium:px-4 medium:pb-4">
            <div>
                <p class="body-xs">
                    {{ productData.vendor }}
                </p>
                <p class="body-m">
                    {{ productData.name }}
                </p>
                <div class="font-bold body-m">
                    <p v-if="productData.variants.length >= 2 && productData.price_min">
                        <span>{{ $root.config.translations.product_cards.from_price_before }}</span> <span>{{ productData.price_min }}</span>
                    </p>
                    <p
                        v-else-if="productData.compare_at_price && productData.price_currency"
                        class="flex flex-wrap items-center gap-1"
                    >
                        <s class="opacity-70">
                            {{ productData.compare_at_price }}
                        </s>
                        <span>
                            {{ productData.price_currency }}
                        </span>
                    </p>
                    <p v-else>
                        {{ productData.price_currency }}
                    </p>
                </div>
            </div>
            <div class="w-full text-center box-border">
                <template v-if="!productData.available">
                    <button type="button" class="button button--small button--full-width" disabled>
                        {{ $root.config.translations.product_cards.sold_out }}
                    </button>
                </template>
                <template v-else-if="productData.variants.length >= 2">
                    <button
                        type="button"
                        class="button button--primary button--small button--full-width"
                        @click="openBundleModal(true)"
                    >
                        {{ $root.config.translations.product_cards.choose_options }}
                    </button>
                </template>
                <template v-else>
                    <template v-if="productIsInBundle">
                        <button
                            type="button"
                            class="button button--secondary button--small button--full-width"
                            @click="removeProductFromBundle(bundleIndex)"
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="M8.14806 1.07422H5.85176C5.29948 1.07422 4.85176 1.52193 4.85176 2.07422V2.22237H9.14806V2.07422C9.14806 1.52193 8.70034 1.07422 8.14806 1.07422ZM5.85176 0.0742188C4.74719 0.0742188 3.85176 0.96965 3.85176 2.07422V2.22237V2.40755H0.703613C0.427471 2.40755 0.203613 2.63141 0.203613 2.90755C0.203613 3.1837 0.427471 3.40755 0.703613 3.40755H1.96287V3.59274L1.96287 11.9261C1.96287 13.0306 2.8583 13.9261 3.96287 13.9261L10.0369 13.9261C11.1415 13.9261 12.0369 13.0306 12.0369 11.9261L12.0369 3.59274V3.40755H13.2962C13.5723 3.40755 13.7962 3.1837 13.7962 2.90755C13.7962 2.63141 13.5723 2.40755 13.2962 2.40755H10.1481V2.22237V2.07422C10.1481 0.969649 9.25263 0.0742188 8.14806 0.0742188H5.85176ZM10.0369 12.9261L3.96287 12.9261C3.41059 12.9261 2.96287 12.4784 2.96287 11.9261L2.96287 3.59274L11.0369 3.59274L11.0369 11.9261C11.0369 12.4784 10.5892 12.9261 10.0369 12.9261Z" />
                            </svg>
                            <span>
                                {{ $root.config.translations.product_cards.remove_from_bundle }}
                            </span>
                        </button>
                    </template>
                    <template v-else>
                        <button
                            type="button"
                            class="button button--primary button--small button--full-width"
                            @click="addToBundle(productData, 1, productData.id)"
                        >
                            {{ $root.config.translations.product_cards.add_to_bundle }}
                        </button>
                    </template>
                </template>
                <div class="mt-4 medium:mt-[0.625rem]">
                    <button
                        type="button"
                        class="link body-s font-bold text-primary"
                        @click="openBundleModal(false)"
                    >
                        {{ $root.config.translations.product_cards.quick_view }}
                    </button>
                </div>
            </div>
        </div>
    </div>
    <AddToBundleOverlay
        ref="addToBundleModal"
        :currencySymbol="currencySymbol"
        :currentVariantId="currentVariantId"
        :isLightView="modalLightView"
        :productData="productData"
        :productQuantity="currentQuantity"
        @added-to-bundle="addToBundle"
        @variant-selected="variantSelected"
        @quantity-changed="quantityChanged"
    />
</template>

<script>
    import AddToBundleOverlay from '@/apps/bundle-builder/components/AddToBundleOverlay.vue';

    export default {
        components: {
            AddToBundleOverlay
        },
        emits: [
            'added-to-bundle',
            'removed-bundle-item'
        ],
        props: {
            bundleIndex: {
                type: Number,
                default () {
                    return 0;
                }
            },
            currencySymbol: {
                type: Array,
                default () {
                    return [];
                }
            },
            productData: {
                type: Object,
                default () {
                    return {};
                }
            },
            selectedVariants: {
                type: Array,
                default() {
                    return [];
                }
            }
        },
        data () {
            return {
                currentVariantId: null,
                currentQuantity: 1,
                modalLightView: null
            }
        },
        computed: {
            productIsInBundle () {
                if (this.productData.variants.find((variant) => this.selectedVariants.includes(variant.id))) {
                    return true;
                }
                return false;
            }
        },
        methods: {
            addToBundle (product, quantity, variantId, isOverlay) {
                this.$emit('added-to-bundle', product, quantity, variantId, isOverlay);

                this.closeBundleModal();
            },
            closeBundleModal () {
                if (this.$refs.addToBundleModal?.$refs.modal) {
                    this.$refs.addToBundleModal.$refs.modal.closeDialog();
                }
            },
            openBundleModal (isLightView) {
                this.modalLightView = isLightView;

                if (this.$refs.addToBundleModal?.$refs.modal) {
                    this.$refs.addToBundleModal.$refs.modal.openDialog();
                }
            },
            removeProductFromBundle (index) {
                this.$emit('removed-bundle-item', index);
            },
            variantSelected (id) {
                this.currentVariantId = id;
            },
            quantityChanged (qty) {
                this.currentQuantity += qty;
            }
        }
    };
</script>
